import { useState } from 'react';
import { useDevice, useOptimisticList } from '@/hooks';
import { contactService } from '@/api/services';
import type { NAContactEntryObject } from '@/api/models';
import { Button } from '@/components/common/Button/Button';
import { Modal } from '@/components/common/Modal/Modal';
import { StatusBadge } from '@/components/common/StatusBadge/StatusBadge';
import { EmptyState } from '@/components/common/EmptyState/EmptyState';
import { PageSpinner } from '@/components/common/Spinner/Spinner';
import { MAX_CONTACTS, MAX_ADMIN_CONTACTS } from '@/config/constants';

const contactKey = (c: NAContactEntryObject) => c.cellStr;

// Mirrors the backend's Globals.formatCellNumber exactly: a bare 10-digit
// US number with no country code gets "1" prepended before it's stored, so
// the server always returns it as 11 digits. Without applying the same
// normalization here, the optimistic entry's cellStr never matches the
// server-confirmed one, and useOptimisticList shows a duplicate row for the
// whole 10s grace period before evicting the stale local copy.
function formatCellNumber(cellNumber: string): string {
  if (cellNumber.length === 10 && cellNumber[0] !== '1') {
    return `1${cellNumber}`;
  }
  return cellNumber;
}

export function ContactListPage() {
  const { selectedDeviceInfo, selectedDevice, deviceInfoLoading, refreshDeviceInfo } = useDevice();
  const deviceUUID = selectedDevice?.deviceProperties.deviceUUID ?? '';
  const serverContacts: NAContactEntryObject[] = selectedDeviceInfo?.contactList ?? [];
  // A just-added contact can briefly vanish (and a just-removed one briefly
  // reappear) if an in-flight poll returns a contactList snapshot from before
  // the write propagated — same class of problem as the Configuration page's
  // MT pending status, just for a plain REST-backed list instead of an MT
  // queue. Holds the optimistic add/remove until the server list agrees, or
  // up to a 10s grace period.
  const { mergedList: contacts, addOptimistic, removeOptimistic } = useOptimisticList(serverContacts, contactKey);
  const isAdmin = selectedDeviceInfo?.userInfo?.isAdmin ?? false;

  const adminAssignedCount = contacts.filter((c) => c.isAssignedByAdmin).length;

  // Add modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');

  // Remove modal
  const [removeTarget, setRemoveTarget] = useState<NAContactEntryObject | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);

  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddError('');

    const cleaned = formatCellNumber(phoneNumber.replace(/[^0-9+]/g, ''));
    if (cleaned.length < 10) {
      setAddError('Please enter a valid phone number (at least 10 digits).');
      return;
    }
    if (contacts.length >= MAX_CONTACTS) {
      setAddError(`Maximum of ${MAX_CONTACTS} contacts reached.`);
      return;
    }
    if (adminAssignedCount >= MAX_ADMIN_CONTACTS) {
      setAddError(`Maximum of ${MAX_ADMIN_CONTACTS} admin-assigned contacts reached.`);
      return;
    }

    setAddLoading(true);
    try {
      const newContact: NAContactEntryObject = {
        cellStr: cleaned,
        isAdmin: false,
        isEmergencyContact: false,
        isAssignedByAdmin: true,
        isXCaliber: false,
      };
      await contactService.updateContactList({
        deviceUUID,
        addEntries: true,
        entries: [newContact],
      });
      addOptimistic(newContact);
      refreshDeviceInfo();
      setShowAddModal(false);
      setPhoneNumber('');
      setFeedback({ type: 'success', message: 'Contact added.' });
    } catch {
      setAddError('Failed to add contact.');
    } finally {
      setAddLoading(false);
    }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    setRemoveLoading(true);
    try {
      await contactService.updateContactList({
        deviceUUID,
        removeEntries: true,
        entries: [
          {
            cellStr: removeTarget.cellStr,
            isAdmin: removeTarget.isAdmin,
            isEmergencyContact: removeTarget.isEmergencyContact,
            isAssignedByAdmin: removeTarget.isAssignedByAdmin,
            isXCaliber: removeTarget.isXCaliber,
          },
        ],
      });
      removeOptimistic(contactKey(removeTarget));
      refreshDeviceInfo();
      setRemoveTarget(null);
      setFeedback({ type: 'success', message: 'Contact removed.' });
    } catch {
      setFeedback({ type: 'error', message: 'Failed to remove contact.' });
    } finally {
      setRemoveLoading(false);
    }
  };

  if (deviceInfoLoading) return <PageSpinner />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Contacts</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Manage authorized contacts for this device
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500 dark:text-slate-400">
            {contacts.length} / {MAX_CONTACTS} total
          </span>
          {isAdmin && (
            <Button
              onClick={() => setShowAddModal(true)}
              disabled={contacts.length >= MAX_CONTACTS || adminAssignedCount >= MAX_ADMIN_CONTACTS}
            >
              Add Contact
            </Button>
          )}
        </div>
      </div>

      {/* Feedback */}
      {feedback && (
        <div
          className={`rounded-lg border p-3 text-sm ${
            feedback.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400'
              : 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400'
          }`}
        >
          {feedback.message}
        </div>
      )}

      {/* Contact List */}
      {contacts.length === 0 ? (
        <EmptyState
          title="No contacts configured"
          description="No contacts are associated with this device."
        />
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white dark:border-navy-700 dark:bg-navy-900">
          <div className="divide-y divide-slate-100 dark:divide-navy-800">
            {contacts.map((contact, idx) => (
              <div
                key={`${contact.cellStr}-${idx}`}
                className="flex items-center justify-between px-5 py-4"
              >
                <div className="flex items-center gap-4">
                  {/* Avatar circle */}
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-mono text-sm font-medium text-slate-800 dark:text-white">
                      {contact.cellStr}
                    </p>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      <StatusBadge
                        label={contact.isAdmin ? 'Device Admin' : 'User'}
                        variant={contact.isAdmin ? 'info' : 'neutral'}
                      />
                    </div>
                  </div>
                </div>
                {isAdmin && contact.isAssignedByAdmin && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setRemoveTarget(contact)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Contact Modal */}
      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Add Contact">
        <form onSubmit={handleAdd} className="space-y-4">
          {addError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
              {addError}
            </div>
          )}
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Phone Number
            </label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
              placeholder="+1 555-123-4567"
            />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Enter the full phone number including country code.
            </p>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={addLoading}>
              Add Contact
            </Button>
          </div>
        </form>
      </Modal>

      {/* Remove Confirmation Modal */}
      <Modal isOpen={!!removeTarget} onClose={() => setRemoveTarget(null)} title="Remove Contact">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Are you sure you want to remove <strong className="text-slate-800 dark:text-white">{removeTarget?.cellStr}</strong> from the contact list?
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setRemoveTarget(null)}>
            Cancel
          </Button>
          <Button variant="danger" loading={removeLoading} onClick={handleRemove}>
            Remove
          </Button>
        </div>
      </Modal>
    </div>
  );
}
